import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, Typography, TextField, CircularProgress, InputAdornment } from '@mui/material';
import { Search, Add, Remove } from '@mui/icons-material';

/**
 * DiffMiniMap - List of change blocks with text preview
 *
 * Props:
 * - backupId: current backup ID
 * - compareId: comparison backup ID
 * - height: total height (default 400) - supports string (calc) or number
 * - onClickBlock: callback when user clicks on a block (receives line_index)
 * - stats: object with {added, removed, unchanged} counts
 * - api: axios instance for API calls
 * - routeBase: base route for API calls
 */
export default function DiffMiniMap({
  backupId,
  compareId,
  height = 400,
  onClickBlock,
  stats = {},
  api,
  routeBase
}) {
  const containerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalBlocks, setTotalBlocks] = useState(0);

  // Search with debounce
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);

  const COLORS = {
    add: '#2da44e',
    addBg: '#e6ffec',
    remove: '#cf222e',
    removeBg: '#ffebe9',
  };

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  // Load blocks when backup/compare changes or search changes
  const loadBlocks = useCallback((reset = true) => {
    if (!backupId || !compareId || !api || !routeBase) return;

    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }

    const currentOffset = reset ? 0 : offset;
    const queryParam = debouncedQuery ? `&q=${encodeURIComponent(debouncedQuery)}` : '';

    api.get(`${routeBase}/${backupId}/diff/${compareId}/blocks?offset=${currentOffset}&limit=50${queryParam}`)
      .then(res => {
        const data = res.data.data;
        if (reset) {
          setBlocks(data.blocks || []);
        } else {
          setBlocks(prev => [...prev, ...(data.blocks || [])]);
        }
        setHasMore(data.has_more);
        setTotalBlocks(data.total_blocks);
        setOffset(currentOffset + (data.blocks?.length || 0));
        setLoading(false);
        setLoadingMore(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [backupId, compareId, api, routeBase, debouncedQuery, offset]);

  // Initial load and on search change
  useEffect(() => {
    loadBlocks(true);
  }, [backupId, compareId, debouncedQuery]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadBlocks(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadBlocks]);

  const handleBlockClick = (block) => {
    if (onClickBlock) {
      onClickBlock(block.line_index);
    }
  };

  if (!backupId || !compareId) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        width: 280,
        minWidth: 280,
        height: height,
        overflow: 'hidden',
      }}
    >
      {/* Header with stats */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Mudanças
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          {stats.added > 0 && (
            <Typography variant="caption" sx={{ color: COLORS.add, fontWeight: 'bold' }}>
              +{stats.added}
            </Typography>
          )}
          {stats.added > 0 && stats.removed > 0 && (
            <Typography variant="caption" color="text.secondary">/</Typography>
          )}
          {stats.removed > 0 && (
            <Typography variant="caption" sx={{ color: COLORS.remove, fontWeight: 'bold' }}>
              -{stats.removed}
            </Typography>
          )}
          {totalBlocks > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {blocks.length}/{totalBlocks} blocos
            </Typography>
          )}
        </Box>

        {/* Search field with debounce */}
        <TextField
          size="small"
          placeholder="Filtrar mudanças..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Search fontSize="small" color="action" />
                )}
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
        />
      </Box>

      {/* Blocks list */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 0.5,
        }}
      >
        {loading && blocks.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : blocks.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {debouncedQuery ? 'Nenhum resultado' : 'Sem mudanças'}
            </Typography>
          </Box>
        ) : (
          <>
            {blocks.map((block, index) => (
              <Box
                key={`${block.line_index}-${index}`}
                onClick={() => handleBlockClick(block)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  p: 0.75,
                  mb: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: block.type === 'add' ? COLORS.addBg : COLORS.removeBg,
                  border: '1px solid transparent',
                  '&:hover': {
                    border: `1px solid ${block.type === 'add' ? COLORS.add : COLORS.remove}`,
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: block.type === 'add' ? COLORS.add : COLORS.remove,
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  {block.type === 'add' ? (
                    <Add sx={{ fontSize: 14 }} />
                  ) : (
                    <Remove sx={{ fontSize: 14 }} />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      fontSize: '11px',
                      color: block.type === 'add' ? COLORS.add : COLORS.remove,
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {block.content || '(vazio)'}
                  </Typography>
                  {block.count > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                      +{block.count - 1} linhas
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            {/* Load more trigger */}
            {hasMore && (
              <Box
                ref={loadMoreRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 1,
                }}
              >
                {loadingMore ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Carregar mais...
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
